import {
    Box,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type ListSizeConfig, type ListSizeMode, compileListSize, decompileListSize } from '../condition-mapper';
import type { FieldCondition } from '../product-requirements-config.dto';
import type { FieldOption } from './ConditionBuilder';

interface Props {
    value: FieldCondition | null;
    /** Sibling fields whose value can drive the row count. */
    fieldOptions: FieldOption[];
    onChange: (value: FieldCondition | null) => void;
}

/**
 * How many rows a LIST has: free-add (user adds/removes), a fixed count, or a
 * count read from a sibling field (e.g. a "meter count" SELECT).
 */
export default function ListSizeEditor({ value, fieldOptions, onChange }: Props) {
    const { t } = useTranslation();

    // Lazy init: seed once from the incoming value.
    const [config, setConfig] = useState<ListSizeConfig>(() => decompileListSize(value) ?? { mode: 'free' });

    function emit(next: ListSizeConfig) {
        setConfig(next);
        onChange(compileListSize(next));
    }

    return (
        <Box>
            <Typography variant="subtitle2">{t('Number of rows')}</Typography>
            <RadioGroup
                row
                value={config.mode}
                onChange={(e) => {
                    const mode = e.target.value as ListSizeMode;
                    if (mode === 'free') emit({ mode: 'free' });
                    else if (mode === 'fixed') emit({ mode: 'fixed', fixed: config.fixed ?? 1 });
                    else emit({ mode: 'fromField', field: config.field ?? fieldOptions[0]?.code ?? '' });
                }}
            >
                <FormControlLabel value="free" control={<Radio size="small" />} label={t('Free add / remove')} />
                <FormControlLabel value="fixed" control={<Radio size="small" />} label={t('Fixed count')} />
                <FormControlLabel value="fromField" control={<Radio size="small" />} label={t('From another field')} />
            </RadioGroup>

            {config.mode === 'fixed' && (
                <TextField
                    size="small"
                    type="number"
                    label={t('Rows')}
                    value={config.fixed ?? 0}
                    onChange={(e) => emit({ mode: 'fixed', fixed: Math.max(0, Number(e.target.value) || 0) })}
                />
            )}

            {config.mode === 'fromField' && (
                <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel>{t('Count field')}</InputLabel>
                    <Select
                        label={t('Count field')}
                        value={config.field ?? ''}
                        onChange={(e) => emit({ mode: 'fromField', field: e.target.value })}
                    >
                        {fieldOptions.map((f) => (
                            <MenuItem key={f.code} value={f.code}>
                                {f.label} ({f.code})
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}
        </Box>
    );
}
